import React from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  ArrowRightLeft,
  ClipboardCheck,
  Shield,
  Droplets,
  Zap,
  Briefcase,
} from 'lucide-react';
import { PILLARS } from '../../config/pillars';

const ICON_MAP = {
  Target,
  ArrowRightLeft,
  ClipboardCheck,
  Shield,
  Droplets,
  Zap,
  Briefcase,
};

/**
 * PillarStrip — renders the seven pillars as numbered steps.
 * Variants:
 *   hero    — large, horizontal on lg+ with connector lines, vertical below
 *   compact — single-line horizontal, smaller labels
 *   footer  — white-on-dark, compact
 *
 * Props:
 *   variant  'hero' | 'compact' | 'footer'
 *   active   pillar key to highlight (optional)
 */
const PillarStrip = ({ variant = 'hero', active }) => {
  const isFooter = variant === 'footer';
  const isCompact = variant === 'compact';

  if (isFooter) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {PILLARS.map((p, i) => (
          <React.Fragment key={p.key}>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">
              {String(p.n).padStart(2, '0')} {p.short}
            </span>
            {i < PILLARS.length - 1 && (
              <span className="text-zinc-500 text-[10px]">&rarr;</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  if (isCompact) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {PILLARS.map((p, i) => {
          const Icon = ICON_MAP[p.icon];
          const isActive = active === p.key;
          return (
            <React.Fragment key={p.key}>
              <Link
                to={`/products#${p.key}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-mono uppercase tracking-[0.1em] transition-all ${
                  isActive
                    ? 'border-[#3832e3] text-[#3832e3] bg-[#3832e3]/5'
                    : 'border-zinc-300 text-zinc-600 hover:border-[#4D4D4D] hover:text-black'
                }`}
              >
                {Icon && <Icon className="w-3 h-3" />}
                <span className="hidden sm:inline">{String(p.n).padStart(2, '0')}</span>
                {p.short}
              </Link>
              {i < PILLARS.length - 1 && (
                <span className="text-zinc-300 text-xs hidden lg:inline">&rarr;</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // hero variant
  return (
    <div className="w-full">
      {/* Desktop: horizontal strip */}
      <div className="hidden lg:flex items-stretch justify-between gap-0">
        {PILLARS.map((p, i) => {
          const Icon = ICON_MAP[p.icon];
          const isActive = active === p.key;
          return (
            <React.Fragment key={p.key}>
              <Link
                to={`/products#${p.key}`}
                className={`flex-1 group flex flex-col items-center text-center px-3 py-5 border transition-all ${
                  isActive
                    ? 'border-[#3832e3] bg-[#3832e3]/5'
                    : 'border-zinc-300 hover:border-[#4D4D4D]'
                }`}
              >
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-2 ${
                  isActive ? 'text-[#3832e3]' : 'text-zinc-400'
                }`}>
                  {String(p.n).padStart(2, '0')}
                </span>
                {Icon && (
                  <Icon className={`w-5 h-5 mb-2 ${
                    isActive ? 'text-[#3832e3]' : 'text-zinc-500 group-hover:text-black'
                  }`} />
                )}
                <span className={`font-mono text-xs uppercase tracking-[0.1em] font-medium ${
                  isActive ? 'text-[#3832e3]' : 'text-black'
                }`}>
                  {p.short}
                </span>
                <span className="text-[10px] text-zinc-500 mt-1 leading-tight font-sans hidden xl:block">
                  {p.label}
                </span>
              </Link>
              {i < PILLARS.length - 1 && (
                <div className="flex items-center px-1">
                  <div className="w-6 h-px bg-zinc-300" />
                  <span className="text-zinc-300 text-xs">&rarr;</span>
                  <div className="w-6 h-px bg-zinc-300" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile: vertical stack */}
      <div className="lg:hidden flex flex-col gap-0">
        {PILLARS.map((p, i) => {
          const Icon = ICON_MAP[p.icon];
          const isActive = active === p.key;
          return (
            <React.Fragment key={p.key}>
              <Link
                to={`/products#${p.key}`}
                className={`flex items-center gap-3 px-4 py-3 border transition-all ${
                  isActive
                    ? 'border-[#3832e3] bg-[#3832e3]/5'
                    : 'border-zinc-300 hover:border-[#4D4D4D]'
                } ${i > 0 ? '-mt-px' : ''}`}
              >
                <span className={`font-mono text-xs ${isActive ? 'text-[#3832e3]' : 'text-zinc-400'}`}>
                  {String(p.n).padStart(2, '0')}
                </span>
                {Icon && (
                  <Icon className={`w-4 h-4 ${isActive ? 'text-[#3832e3]' : 'text-zinc-500'}`} />
                )}
                <span className={`font-mono text-xs uppercase tracking-[0.1em] font-medium ${
                  isActive ? 'text-[#3832e3]' : 'text-black'
                }`}>
                  {p.label}
                </span>
              </Link>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default PillarStrip;
